import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Sparkles, Loader2, Lock, Flame, Scroll, Swords, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { HighlightedText } from "@/components/HighlightedText";
import D20Icon from "@/components/D20Icon";
import { useSeoTitle } from "@/lib/useSeoTitle";

const TRIAL_ID_KEY = "saga_trial_id";
const FALLBACK_ARCHETYPES = [
  { id: "rogue", name: "Rogue", role: "a quick-thinking scout who survives by nerve and skill", opening: "You arrive with a useful contact and a reason to keep moving." },
  { id: "warrior", name: "Warrior", role: "a seasoned protector with the resolve to face danger directly", opening: "You arrive prepared to stand between trouble and those who cannot face it alone." },
  { id: "wizard", name: "Wizard", role: "a learned seeker of unusual answers and difficult truths", opening: "You arrive carrying hard-won knowledge and a question the realm cannot ignore." },
];

const TRIAL_ARCHETYPE_KEY = "saga_trial_archetype";

const ARCHETYPE_ICONS = {
  rogue: Flame,
  wizard: Scroll,
  warrior: Swords,
};

function archetypeIcon(id = "") {
  const key = String(id).replace(/^class:/, "").toLowerCase();
  if (key.includes("mage") || key.includes("wizard") || key.includes("sorcer") || key.includes("occult") || key.includes("onmyoji")) return Scroll;
  if (key.includes("fighter") || key.includes("warrior") || key.includes("samurai") || key.includes("berserk") || key.includes("gunslinger")) return Swords;
  return ARCHETYPE_ICONS[key] || Flame;
}

function newTrialId() {
  // Crypto-safe enough; backend just needs a stable opaque id 8–64 chars.
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `t-${crypto.randomUUID()}`;
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function TrialPlay() {
  const nav = useNavigate();
  useSeoTitle({
    title: "Free 5-turn trial | TalesTalesTales",
    description: "Try TalesTalesTales for 5 turns with no signup and no credit card. Type an action; the AI writes the illustrated scene.",
    path: "/trial",
  });
  const [archetypes, setArchetypes] = useState(FALLBACK_ARCHETYPES);
  const [realms, setRealms] = useState([]);
  const [realmId, setRealmId] = useState("");
  const [heroName, setHeroName] = useState("");
  const [archetypeId, setArchetypeId] = useState("");
  const [trial, setTrial] = useState(null); // {trial_id, turns_used, turns_remaining, max_turns, messages, hero_name}
  const [starting, setStarting] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [diceRolling, setDiceRolling] = useState(false);
  const [pendingDice, setPendingDice] = useState(null);
  const [pendingDieLabel, setPendingDieLabel] = useState("d20");
  const [rolledMessages, setRolledMessages] = useState(new Set());
  const [showEndModal, setShowEndModal] = useState(false);
  const endRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const selectedRealm = realms.find((realm) => realm.id === realmId);
  const availableArchetypes = archetypes;

  // Load archetypes + existing trial state if any
  useEffect(() => {
    api.get("/trial/realms").then((r) => {
      setRealms(r.data || []);
      if (r.data?.[0]?.id) setRealmId(r.data[0].id);
    }).catch(() => {});
    const existingId = localStorage.getItem(TRIAL_ID_KEY);
    if (existingId) {
      api.get(`/trial/state?trial_id=${encodeURIComponent(existingId)}`)
        .then((r) => {
          setTrial(r.data);
          setMessages(r.data.messages || []);
          if (r.data.turns_used >= r.data.max_turns) setShowEndModal(true);
        })
        .catch(() => {
          localStorage.removeItem(TRIAL_ID_KEY);
        });
    }
  }, []);

  // Ask the backend for every class this specific realm permits. The
  // catalog includes setting-native jobs such as Onmyoji or Netrunner,
  // not merely the original three sample archetypes.
  useEffect(() => {
    if (!realmId) return;
    api.get(`/trial/archetypes?realm_id=${encodeURIComponent(realmId)}`)
      .then((r) => {
        const catalog = Array.isArray(r.data) ? r.data.filter((entry) => entry?.id && entry?.name) : [];
        setArchetypes(catalog.length ? catalog : FALLBACK_ARCHETYPES);
      })
      .catch(() => setArchetypes(FALLBACK_ARCHETYPES));
  }, [realmId]);

  useEffect(() => {
    if (archetypeId && !availableArchetypes.some((archetype) => archetype.id === archetypeId)) {
      setArchetypeId("");
    }
  }, [realmId, archetypeId, availableArchetypes]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || userScrolledUpRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streamingText]);

  const handleChatScroll = (e) => {
    const el = e.currentTarget;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distFromBottom > 80;
  };

  const startTrial = async () => {
    if (!realmId) { toast.error("Choose a realm for your trial."); return; }
    if (heroName.trim().length < 2) { toast.error("Give your hero a name before beginning."); return; }
    if (!archetypeId) { toast.error("Choose a path for your hero."); return; }
    setStarting(true);
    try {
      const trialId = localStorage.getItem(TRIAL_ID_KEY) || newTrialId();
      localStorage.setItem(TRIAL_ID_KEY, trialId);
      localStorage.setItem(TRIAL_ARCHETYPE_KEY, archetypeId);
      const r = await api.post("/trial/start", { trial_id: trialId, archetype: archetypeId, realm_id: realmId, hero_name: heroName.trim() });
      setTrial(r.data);
      setMessages(r.data.messages || []);
      if (r.data.turns_used >= r.data.max_turns) setShowEndModal(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not begin trial. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  const rollDieInline = async (die = "d20", messageId = null) => {
    if (messageId && rolledMessages.has(messageId)) {
      toast("That roll has already been cast — first roll is final.");
      return;
    }
    const m = String(die).toLowerCase().trim().match(/^(\d*)d(\d+)$/);
    let sides = 20, count = 1;
    if (m) {
      count = parseInt(m[1] || "1", 10);
      sides = parseInt(m[2], 10);
    }
    setDiceRolling(true);
    setPendingDice(null);
    setPendingDieLabel(die);
    try {
      const rolls = [];
      for (let i = 0; i < Math.max(1, Math.min(count, 2)); i++) {
        const r = await api.post(`/dice/roll?sides=${sides}&label=d${sides}`);
        rolls.push(r.data.roll);
      }
      const final = rolls.reduce((a, b) => a + b, 0);
      setTimeout(() => {
        setDiceRolling(false);
        setPendingDice(final);
        if (messageId) setRolledMessages((prev) => new Set(prev).add(messageId));
        if (sides === 20 && final === 20) toast.success(`CRITICAL! d20 = 20`);
        else if (sides === 20 && final === 1) toast.error(`FUMBLE! d20 = 1`);
        else toast(`${die}: ${final}`);
      }, 700);
    } catch {
      setDiceRolling(false);
      toast.error("The dice fled.");
    }
  };

  const sendTurn = async (overrideAction = null) => {
    const action = (overrideAction !== null ? overrideAction : input).trim();
    if (!action || streaming || !trial) return;
    if (trial.turns_used >= trial.max_turns) {
      setShowEndModal(true);
      return;
    }

    setStreaming(true);
    setStreamingText("");

    const tempPlayer = {
      id: `tmp-${Date.now()}`,
      role: "player",
      content: pendingDice ? `${action}\n[Player rolled a d20: ${pendingDice}]` : action,
      created_at: new Date().toISOString(),
      dice_roll: pendingDice,
    };
    setMessages((m) => [...m, tempPlayer]);
    const usedDice = pendingDice;
    setInput("");
    setPendingDice(null);

    const params = new URLSearchParams({ trial_id: trial.trial_id, action });
    if (usedDice !== null && usedDice !== undefined) params.set("dice", String(usedDice));
    const url = `${API_BASE}/trial/stream?${params.toString()}`;
    const es = new EventSource(url);

    let acc = "";
    es.addEventListener("player", (ev) => {
      const p = JSON.parse(ev.data);
      setMessages((m) => m.map((x) => (x.id === tempPlayer.id ? p : x)));
    });
    es.addEventListener("delta", (ev) => {
      const d = JSON.parse(ev.data);
      acc += d.content;
      setStreamingText(acc);
    });
    es.addEventListener("done", (ev) => {
      const dm = JSON.parse(ev.data);
      setMessages((m) => [...m, {
        id: dm.id,
        role: "dm",
        content: dm.content,
        choices: dm.choices || [],
        roll_request: dm.roll_request,
        created_at: dm.created_at,
      }]);
      setStreamingText("");
      setStreaming(false);
      setTrial((t) => t ? {
        ...t,
        turns_used: dm.turns_used,
        turns_remaining: dm.turns_remaining,
      } : t);
      if (dm.trial_complete) {
        setShowEndModal(true);
      }
      es.close();
    });
    es.addEventListener("stream_error", (ev) => {
      try {
        const err = JSON.parse(ev.data);
        toast.error(err.message || "The narrator's voice faltered.");
      } catch {
        toast.error("The narrator's voice faltered.");
      }
      // Remove temp player message
      setMessages((m) => m.filter((x) => x.id !== tempPlayer.id));
      setStreaming(false);
      setStreamingText("");
      es.close();
    });
    es.onerror = () => {
      es.close();
      setStreaming(false);
      setStreamingText("");
    };
  };

  const onChoiceClick = (choice) => {
    setInput(choice);
    setTimeout(() => sendTurn(choice), 50);
  };

  // ----- Render: Archetype picker (no trial started yet) -----
  if (!trial) {
    return (
      <div data-testid="trial-picker-page" className="min-h-screen bg-[#0f0d11]">
        <Nav />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="text-center rise">
            <div className="font-ui text-[10px] sm:text-xs tracking-[0.4em] text-amber-400/80 uppercase mb-3">
              ⚜ Free 5-Turn Adventure · No Account Needed ⚜
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-amber-50 leading-tight">
              Choose a realm. Forge your hero.
            </h1>
            <p className="mt-5 font-body text-base text-amber-100/70 max-w-xl mx-auto leading-relaxed">
              Sample the game with five free turns — strictly family-friendly and PG-13.
              Like it? Create a free account to keep playing, save your campaign, and unlock all worlds.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            <div className="sm:col-span-3 parchment gold-border rounded-lg p-5 grid sm:grid-cols-2 gap-4">
              <label className="font-ui text-xs uppercase tracking-widest text-amber-200/75">
                Choose a realm
                <select
                  data-testid="trial-realm-select"
                  value={realmId}
                  onChange={(e) => {
                    setRealmId(e.target.value);
                    setArchetypeId("");
                  }}
                  className="mt-2 w-full rounded-md border border-amber-700/30 bg-[#15121a] px-3 py-2.5 font-body text-amber-100"
                >
                  {realms.map((realm) => <option key={realm.id} value={realm.id}>{realm.title} - {realm.setting}</option>)}
                </select>
              </label>
              <label className="font-ui text-xs uppercase tracking-widest text-amber-200/75">
                Forge your hero
                <Input data-testid="trial-hero-name" value={heroName} onChange={(e) => setHeroName(e.target.value)} maxLength={60} placeholder="Hero name" className="mt-2 bg-[#15121a] border-amber-700/30 text-amber-100 font-body" />
              </label>
            </div>
            {availableArchetypes.map((a, i) => {
              const Icon = archetypeIcon(a.id);
              return (
                <button
                  key={a.id}
                  data-testid={`trial-pick-${a.id}`}
                  disabled={starting}
                  onClick={() => setArchetypeId(a.id)}
                  aria-pressed={archetypeId === a.id}
                  className={`parchment gold-border rounded-xl p-6 text-left transition rise hover:candle-glow disabled:opacity-50 flex flex-col ${archetypeId === a.id ? "ring-2 ring-amber-400 border-amber-400" : ""}`}
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <Icon className="w-8 h-8 text-amber-400 mb-3 flicker" />
                  <h3 className="font-display text-2xl text-amber-50 capitalize">{a.id}</h3>
                  <div className="mt-1 font-ui text-[10px] uppercase tracking-widest text-amber-300/70">{a.char_class}</div>
                  <p className="font-body text-sm text-amber-100/70 mt-2 leading-relaxed flex-1">
                    {a.role}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 text-amber-300 font-ui text-sm">
                    <Sparkles className="w-4 h-4" />
                    {archetypeId === a.id ? "Chosen path" : "Choose path"}
                  </div>
                </button>
              );
            })}
            {realmId && availableArchetypes.length === 0 && (
              <div className="sm:col-span-3 border border-amber-700/30 bg-[#15121a] px-5 py-4 font-body text-sm text-amber-100/70">
                Preparing this realm&apos;s hero paths...
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              data-testid="trial-begin-btn"
              onClick={startTrial}
              disabled={starting || !realmId || !archetypeId || heroName.trim().length < 2 || availableArchetypes.length === 0}
              className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui min-w-[220px] h-12 candle-glow"
            >
              <Sparkles className="w-4 h-4 mr-2" /> {starting ? "Conjuring..." : "Begin this saga"}
            </Button>
          </div>

          <div className="mt-12 text-center font-body text-xs text-amber-200/50">
            Already have an account?{" "}
            <Link to="/login" className="gold-text hover:text-amber-200">Sign in</Link>
            {" · "}
            <Link to="/" className="hover:text-amber-200 inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ----- Render: Active trial play view -----
  const turnsUsed = trial.turns_used;
  const turnsRemaining = trial.turns_remaining;
  const maxTurns = trial.max_turns;
  const trialOver = turnsUsed >= maxTurns;
  const lastDm = [...messages].reverse().find((m) => m.role === "dm");
  const choices = lastDm?.choices || [];
  const rollRequest = lastDm?.roll_request;

  return (
    <div data-testid="trial-play-page" className="min-h-screen bg-[#0f0d11] flex flex-col">
      <Nav />

      {/* Top trial banner */}
      <div className="border-b border-amber-700/25 bg-[#15121a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Flame className="w-5 h-5 text-amber-400 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-amber-50 text-sm sm:text-base truncate">
                Trial · Playing as <span className="gold-text">{trial.hero_name}</span>
              </div>
              <div className="font-body text-[11px] text-amber-200/60">
                Family-friendly mode · no save · {turnsRemaining > 0 ? "create an account to continue past 5 turns" : "trial complete"}
              </div>
            </div>
          </div>
          <div data-testid="trial-turns-counter" className="flex items-center gap-2">
            <div className="text-right">
              <div className="font-display text-amber-100 text-lg leading-none">
                {turnsUsed}<span className="text-amber-200/50 text-sm">/{maxTurns}</span>
              </div>
              <div className="font-ui text-[9px] tracking-widest text-amber-200/60 uppercase">turns</div>
            </div>
            <div className="w-20 h-2 bg-stone-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${Math.min(100, (turnsUsed / maxTurns) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Chat area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleChatScroll}
        className="flex-1 overflow-y-auto"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {messages.map((m) => (
            <div key={m.id} data-testid={`trial-msg-${m.role}`}>
              {m.role === "dm" ? (
                <div className="parchment gold-border rounded-lg p-5">
                  <div className="font-ui text-[10px] tracking-[0.3em] text-amber-400/80 uppercase mb-2">
                    Dungeon Master
                  </div>
                  <div className="font-body text-amber-50/95 leading-relaxed whitespace-pre-wrap">
                    <HighlightedText text={m.content} />
                  </div>
                  {m.roll_request && (
                    <div className="mt-4 flex items-center gap-3">
                      <Button
                        data-testid={`trial-roll-${m.id}`}
                        onClick={() => rollDieInline(m.roll_request.die || "d20", m.id)}
                        disabled={diceRolling || rolledMessages.has(m.id)}
                        className="bg-amber-700 hover:bg-amber-600 text-amber-50 font-ui"
                      >
                        <D20Icon className="w-4 h-4 mr-2" />
                        {rolledMessages.has(m.id) ? "Rolled" : `Roll ${m.roll_request.die || "d20"}`}
                      </Button>
                      <span className="font-body text-sm text-amber-200/70 italic">
                        {m.roll_request.skill}
                      </span>
                    </div>
                  )}
                  {m === lastDm && choices.length > 0 && !trialOver && !streaming && (
                    <div className="mt-4 space-y-2">
                      {choices.map((c, i) => (
                        <button
                          key={i}
                          data-testid={`trial-choice-${i}`}
                          onClick={() => onChoiceClick(c)}
                          className="w-full text-left rounded-md border border-amber-700/30 hover:border-amber-500/60 bg-[#15121a]/70 hover:bg-amber-900/15 px-3 py-2 font-body text-sm text-amber-100/90 transition"
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-amber-700/20 bg-[#15121a] p-4 ml-6 sm:ml-12">
                  <div className="font-ui text-[10px] tracking-[0.3em] text-amber-300/70 uppercase mb-1">
                    {trial.hero_name}
                  </div>
                  <div className="font-body text-amber-100/90 whitespace-pre-wrap text-sm">
                    {m.content}
                  </div>
                </div>
              )}
            </div>
          ))}

          {streaming && streamingText && (
            <div className="parchment gold-border rounded-lg p-5">
              <div className="font-ui text-[10px] tracking-[0.3em] text-amber-400/80 uppercase mb-2">
                Dungeon Master
              </div>
              <div className="font-body text-amber-50/95 leading-relaxed whitespace-pre-wrap">
                <HighlightedText text={streamingText} />
                <span className="inline-block w-1 h-4 bg-amber-300 ml-1 animate-pulse" />
              </div>
            </div>
          )}
          {streaming && !streamingText && (
            <div className="parchment gold-border rounded-lg p-5 flex items-center gap-3">
              <Loader2 className="w-4 h-4 text-amber-300 animate-spin" />
              <span className="font-body text-amber-200/80 italic text-sm">
                The Dungeon Master gathers their words...
              </span>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Input bar */}
      <div className="border-t border-amber-700/25 bg-[#15121a]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          {pendingDice !== null && (
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-900/30 border border-amber-500/40 font-ui text-xs text-amber-200">
              <D20Icon className="w-3 h-3" /> {pendingDieLabel} = {pendingDice}
            </div>
          )}
          <div className="flex gap-2">
            <Textarea
              data-testid="trial-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  sendTurn();
                }
              }}
              placeholder={trialOver ? "Your trial has ended — create an account to keep playing." : "What do you do? (Ctrl+Enter to send)"}
              disabled={streaming || trialOver}
              className="bg-[#0f0d11] border-amber-700/30 text-amber-50 font-body resize-none min-h-[60px]"
              rows={2}
            />
            <Button
              data-testid="trial-send-btn"
              onClick={() => sendTurn()}
              disabled={streaming || trialOver || !input.trim()}
              className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-auto px-5"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <div className="mt-2 flex items-center justify-between gap-3 font-ui text-[11px] text-amber-200/55">
            <span>5-turn free trial · family-friendly only · no Mature Realms</span>
            <span>{turnsRemaining} turn{turnsRemaining === 1 ? "" : "s"} left</span>
          </div>
        </div>
      </div>

      {/* End-of-trial modal */}
      {showEndModal && (
        <div
          data-testid="trial-end-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="parchment gold-border rounded-xl p-7 max-w-md w-full text-center candle-glow">
            <Lock className="w-10 h-10 text-amber-400 mx-auto mb-4" />
            <h2 className="font-display text-3xl text-amber-50">Your trial has ended</h2>
            <p className="font-body text-amber-100/80 mt-3 leading-relaxed">
              You&apos;ve played all 5 free turns as <span className="gold-text">{trial.hero_name}</span>.
              Create a free account to <span className="gold-text">continue this saga</span> —
              your transcript will travel with you, no progress lost.
            </p>
            <div className="mt-5 space-y-3">
              <Button
                data-testid="trial-end-register-btn"
                onClick={() => nav(`/register?from=trial&trial_id=${encodeURIComponent(trial.trial_id)}`)}
                className="w-full bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-12"
              >
                <Sparkles className="w-4 h-4 mr-2" /> Claim this saga &amp; continue
              </Button>
              <Button
                data-testid="trial-end-login-btn"
                onClick={() => nav(`/login?trial_id=${encodeURIComponent(trial.trial_id)}`)}
                variant="outline"
                className="w-full border-amber-500/40 text-amber-200 hover:bg-amber-900/20 font-ui h-11 bg-transparent"
              >
                I already have an account
              </Button>
            </div>
            <p className="font-body text-[11px] text-amber-200/50 mt-4 italic">
              Free tier: 10 turns/day · save every campaign · all narrators included.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
